/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getSalesGoalData from "@stoneage/App/Handlers/SalesGoal/getSalesGoalData";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { SalesGoalInfoType } from "@stoneage/App/Store/Reducers/xSalesGoal";
import React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    titleStyle = "titleStyle",
    colorTheme = "colorTheme",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleStyle]: string;
        [fields.colorTheme]: "light" | "dark";
    };
}

const mapStateToProps = (state: CustomAppState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;
    const salesGoalInfo: SalesGoalInfoType = state.xSalesGoal.value;
    const userRole = state.context.session.userRoles;
    const isBasicUser = userRole.indexOf("BasicUser") !== -1;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
        salesGoalInfo,
        userRole,
        isBasicUser,
    };
};

const mapDispatchToProps = {
    getSalesGoalData,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SalesGoalStyles {
    widgetContainer?: InjectableCss;
    infoWrapper?: InjectableCss;
    bottomSection?: InjectableCss;
    title?: TypographyPresentationProps;
    infoItem?: InjectableCss;
    infoItemBar?: InjectableCss;
    fieldLabel?: TypographyPresentationProps;
    fieldValue?: TypographyPresentationProps;
    percentTypo?: TypographyPresentationProps;
    bar?: InjectableCss;
    barPoints?: InjectableCss;
    barPoint?: TypographyPresentationProps;
    barPlaceholder?: InjectableCss;
    barDrawer?: InjectableCss;
}

export const salesGoalStyles: SalesGoalStyles = {
    widgetContainer: {
        css: css`
            height: 100%;
            padding: 20px;
            box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
            border-radius: 2px;
            min-height: 225px;
            display: flex;
            flex-direction: column;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 8px;
        `,
    },
    infoItem: {
        css: css`
            display: flex;
            margin-bottom: 10px;
        `,
    },
    title: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    fieldLabel: {
        size: 16,
        lineHeight: "20px",
        weight: "bold",
        css: css`
            margin-right: 12px;
        `,
    },
    fieldValue: {
        size: 15,
        lineHeight: "20px",
        weight: "normal",
    },
    percentTypo: {
        size: 16,
        lineHeight: "22px",
        weight: "bold",
    },
    bottomSection: {
        css: css`
            text-align: right;
            margin-top: auto;
        `,
    },
    barPoint: {
        size: 12,
        lineHeight: "18px",
        weight: "bold",
    },
    barPoints: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-top: 20px;
        `,
    },
    bar: {
        css: css`
            position: relative;
        `,
    },
    infoItemBar: {
        css: css`
            margin-bottom: 16px;
        `,
    },
    barPlaceholder: {
        css: css`
            width: 100%;
            height: 16px;
            border-radius: 10px;
            background-color: ${getColor("common.ui16")};
            position: absolute;
            top: 0;
            left: 0;
        `,
    },
    barDrawer: {
        css: css`
            height: 16px;
            border-radius: 10px;
            background-color: ${getColor("common.ui15")};
            position: absolute;
            top: 0;
            left: 0;
            z-index: 2;
            max-width: 100%;
        `,
    },
};

const SalesGoal = ({ fields, isAuthenticated, salesGoalInfo, getSalesGoalData, userRole, isBasicUser }: Props) => {
    const styles = salesGoalStyles;
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (!salesGoalInfo || Object.keys(salesGoalInfo).length === 0) {
            getSalesGoalData({
                onSuccess: result => {
                    dispatch({ type: "xSalesGoal/setValue", value: result });
                },
                onError: () => {
                    dispatch({ type: "xSalesGoal/setValue", value: {} });
                },
            });
        }
    }, []);

    const { title, titleStyle, colorTheme } = fields;
    const getTextColor = () => (colorTheme === "light" ? "common.black" : "common.white");
    const getTextFieldValueColor = () => (colorTheme === "light" ? "text.main" : "common.white");

    if (
        !salesGoalInfo ||
        !isAuthenticated ||
        (userRole.indexOf("Administrator") === -1 && userRole.indexOf("Buyer3") === -1) ||
        isBasicUser
    ) {
        return <></>;
    }

    return (
        <StyledWrapper {...styles.widgetContainer}>
            {title && (
                <Typography {...styles.title} variant={titleStyle as keyof ThemeTypography} color={getTextColor()}>
                    {salesGoalInfo.annualSalesGoal > 0
                        ? title || translate("Sales Goal")
                        : translate("Year to Date Orders")}
                </Typography>
            )}
            <StyledWrapper {...styles.infoWrapper}>
                {salesGoalInfo.annualSalesGoal > 0 && salesGoalInfo.annualSalesGoalDisplay && (
                    <StyledWrapper {...styles.infoItem}>
                        <Typography {...styles.fieldLabel} color={getTextColor()}>
                            {translate("Goal")}
                        </Typography>
                        <Typography translate="no" {...styles.fieldValue} color={getTextFieldValueColor()}>
                            {salesGoalInfo.annualSalesGoalDisplay}
                        </Typography>
                    </StyledWrapper>
                )}
                {salesGoalInfo.yearToDateDisplay && (
                    <StyledWrapper
                        {...styles.infoItem}
                        css={css`
                            ${styles.infoItem?.css};
                            margin-bottom: 16px;
                        `}
                    >
                        <Typography {...styles.fieldLabel} color={getTextColor()}>
                            {translate("YTD")}
                        </Typography>
                        <Typography translate="no" {...styles.fieldValue} color={getTextFieldValueColor()}>
                            {salesGoalInfo.yearToDateDisplay}
                        </Typography>
                    </StyledWrapper>
                )}
                {salesGoalInfo.annualSalesGoal > 0 && (
                    <StyledWrapper {...styles.infoItemBar}>
                        <StyledWrapper {...styles.bar}>
                            <StyledWrapper {...styles.barPlaceholder}></StyledWrapper>
                            <StyledWrapper
                                css={css`
                                    ${styles.barDrawer?.css};
                                    width: ${(salesGoalInfo.yearToDate / salesGoalInfo.annualSalesGoal) * 100}%;
                                `}
                            ></StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.barPoints}>
                            <Typography {...styles.barPoint} color={getTextColor()}>
                                {translate("0")}
                            </Typography>
                            <Typography {...styles.barPoint} color={getTextColor()}>
                                {translate("100")}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {salesGoalInfo.annualSalesGoal !== 0 && (
                <StyledWrapper {...styles.bottomSection}>
                    {salesGoalInfo.yearToDate && salesGoalInfo.annualSalesGoal !== 0 ? (
                        <Typography {...styles.percentTypo} color={getTextColor()} translate="no">
                            {((salesGoalInfo.yearToDate / salesGoalInfo.annualSalesGoal) * 100).toFixed(2)}% &nbsp;
                            {translate("of Goal")}
                        </Typography>
                    ) : (
                        <Typography {...styles.percentTypo} color={getTextColor()}>
                            0% &nbsp;
                            {translate("of Goal")}
                        </Typography>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const salesGoal: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SalesGoal),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.colorTheme,
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
        ],
    },
};

export default salesGoal;
